import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1722182877533-7378b60bf1e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhbmNpZW50JTIwbGlicmFyeSUyMGJyaWdodHxlbnwwfHx8fDE3ODcxMzczMTB8MA&ixlib=rb-4.1.0&q=85";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/panel";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-page">
      <div className="hidden lg:block relative grain">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/50" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.png" alt="AktifOkur" className="w-8 h-8 object-contain brightness-200" />
            <span className="font-serif text-3xl text-page tracking-tight">AktifOkur</span>
          </Link>
          <div>
            <p className="font-serif text-4xl text-page italic leading-tight">"Okumak, ruhun beslenmesidir."</p>
            <p className="text-page/70 mt-3">Aktiffelsefe Kitap Topluluğu</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="flex items-center gap-2 lg:hidden mb-8">
            <BookOpen className="text-terra" size={24} />
            <span className="font-serif text-2xl text-ink">AktifOkur</span>
          </Link>
          <h1 className="font-serif text-3xl sm:text-4xl text-ink tracking-tight">{title}</h1>
          <p className="text-ink-2 mt-2">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function GoogleButton() {
  return (
    <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-login-btn"
      className="w-full h-11 rounded-lg border-line text-ink-2 hover:bg-surface-alt gap-2">
      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      Google ile devam et
    </Button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Hoş geldin!");
      navigate("/panel");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Tekrar hoş geldin" subtitle="Kitaplığına ve topluluğa giriş yap.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">E-posta</Label>
          <Input id="email" data-testid="login-email" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" placeholder="ornek@eposta.com" />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <Label htmlFor="password">Şifre</Label>
            <Link to="/sifremi-unuttum" className="text-xs text-terra hover:underline">Şifremi unuttum</Link>
          </div>
          <Input id="password" data-testid="login-password" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full h-11 bg-terra hover:bg-terra-hover text-white rounded-lg">
          {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="h-px bg-line flex-1" /><span className="text-xs text-muted-foreground">veya</span><div className="h-px bg-line flex-1" />
      </div>
      <GoogleButton />
      <p className="text-sm text-center text-ink-2 mt-6">
        Hesabın yok mu? <Link to="/kayit" className="text-terra font-medium hover:underline">Üye ol</Link>
      </p>
    </AuthShell>
  );
}
